import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native'
import { ButtonProps } from 'react-native-paper'
import { z } from 'zod'

import FormFieldText from '@/components/form/FormFieldText'
import NotFound from '@/components/shared/NotFound'
import Snackbar from '@/components/snackbar/Snackbar'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import UserService from '@/services/userService'
import { ApiRoot } from '../../../../../constants'

interface Props {
  buttonStyle?: StyleProp<ViewStyle>
  buttonMode?: ButtonProps['mode']
  formContainerStyle: StyleProp<ViewStyle>
  titleStyle: StyleProp<TextStyle>
}

const UserEmail = ({
  buttonStyle,
  buttonMode,
  formContainerStyle,
  titleStyle,
}: Props) => {
  const { user, isLoading, currentOrganization } = UserService.useCurrentUser()

  const defaultValues = { ...user }

  const form = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form

  useEffect(() => {
    reset(user)
  }, [reset, user])

  const onSubmit = async (data: FormData) => {
    if (!currentOrganization?.id) {
      return
    }

    const response = await fetch(`${ApiRoot()}/change-email`, {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const json = await response.json()

    if (response.ok && json.status === 'EMAIL_VERIFICATION_SENT') {
      Snackbar.info(
        'A verification email has been sent to your new email address.'
      )
    } else if (response.ok) {
      Snackbar.info('Your email address has been changed.')
    } else if (json.status === 'EMAIL_ALREADY_EXISTS_ERROR') {
      Snackbar.error(
        'This email is already in use by another user. Please try a different email.'
      )
    } else {
      Snackbar.error('There was an error changing your email address.')
    }
  }

  if (!isLoading && !user) return <NotFound />

  return (
    <View>
      <UniText style={titleStyle} variant="titleLarge">
        Email Address
      </UniText>
      <View>
        <View style={formContainerStyle}>
          <FormFieldText
            control={control}
            disabled={isLoading}
            errors={errors}
            fieldName="email"
            isLoading={isLoading}
            keyboardType="email-address"
            label="Email"
            required={false}
            style={{ flex: 3 }}
          />
        </View>
        <UniButton
          disabled={isSubmitting || !isDirty}
          mode={buttonMode}
          onPress={handleSubmit(onSubmit)}
          style={buttonStyle}
        >
          Change Email
        </UniButton>
      </View>
    </View>
  )
}

const formSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().toLowerCase(),
})

type FormData = z.infer<typeof formSchema>

export default UserEmail
